import { CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useIsMobile, useToasts, CommonLabelText } from "@brg/ui";
import { FC, useMemo, useEffect } from "react";
import { ContentWrapper, PickerWrapper, Right, SettingsLabel } from "../styles";
import { AppTester } from "supporting";
import { ProjectAuthorsChangeCbT } from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { useTranslation } from "react-i18next";
import { AddButton, ColumnLabelGroup, ProjectEditPageGridRowBase } from "shared/pages/projectEdit/styles";
import { ProjectEditPageGridLabel } from "shared/components/ProjectEditPageGrid";
import { ProjectEditPageGridInput } from "_deziiign/books/pages/BookEdit/styles";
import { Input } from "@brg/input";
import { LabelRedStar } from "shared/pages/designerEditView/styles";
import { bTag, LayoutConst, useModal } from "core";
import { AnchorType, IAddProjectCoverContent, Tag } from "@brg/api";
import { AGEYTOMESH_TAG_ID } from "shared/constants/misc";
import { ProjectAddButton } from "shared/pages/projectEdit/ProjectAddButton";
import { BilingWrapper, ContentAddContainer, ProjectEditGrid } from "shared/pages/projectEdit/styles";
import { MAX_PLACES_TAG_COUNT, MAX_TAG_COUNT } from "shared/pages/projectEdit/constants";
import { BTag } from "core/components";
import { isRequired } from "shared/modals/selectTagsModal/support/isRequired";
import { useRouteMatch } from "react-router-dom";

import { LinkTo } from "shared/constants/linkTo";
import { AuthorT } from "shared/pages/projectEdit/types";
import ColorPicker from "../../ColorPicker";
import ViewTypeController from "shared/components/ViewTypeController/ViewTypeController";
import { BilingualSwitcher } from "shared/components/BilingualSwitcher/BilingualSwitcher";
import { useSelector } from "react-redux";
import { BTagsGridNew } from "../../../../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import { HseContextSelector } from "shared/reducers/hseCurrentContext";
import { Availability } from "../../Availability/Availability";
import PresentationAuthorsSelect from "shared/pages/projectEdit/fields/Designers/PresentationAuthorsSelect";
import CheckboxField from "shared/pages/projectEdit/fields/CheckboxField";
import TagSelect from "shared/pages/projectEdit/fields/Tags/TagSelect";
import RequiredTagSelect from "shared/pages/projectEdit/fields/Tags/RequiredTagSelect";
import { useController, useFormContext } from "react-hook-form";
import { ModerationType } from "shared/pages/teams/const/ModerationTypes";
import AnnotationField from "shared/components/AnnotationField/AnnotationField";
import { BookType } from "_deziiign/books/constants";
import BookChaptersField from "shared/pages/projectEdit/fields/Book/BookChaptersField";
import BookReproductionsField from "shared/pages/projectEdit/fields/Book/BookReproductionsField";
import ProjectStructureField from "shared/pages/projectEdit/fields/ProjectStructureField";
import BookTagsField from "shared/pages/projectEdit/fields/Book/BookTagsField";
import { CommonCheckbox } from "shared/components/CommonCheckbox/CommonCheckbox";


interface IProjectSettingProps {
    project: any;
    vTYPE: string;
    additionalContent?: IAddProjectCoverContent;
    currentColor: string;
    proTypeValue: any;
    proTypes: any[];
    requiredTags: Tag[];
    projectTags: Tag[];
    tagEntity?: string[];
    coAuthors?: AuthorT[];
    contextRequiredTags?: string[];
    hasEducationTag?: boolean;
    tagEntityRequired?: string[];
    isAgeRestricted?: boolean;
    availability?: string;
    isBilingual: boolean;
    isTeamProject?: boolean;
    moderationType?: ModerationType;
    /** Лендинг курса образовательной программы — настраивается как страница, а не как проект */
    isOuterCourse?: boolean;
    toggleAgeRestricted?: (e: any) => void;
    onColorChange: (val: string) => void;
    onSetAvailability?: (availability: string) => void;
    onCoauthorChange?: (data: ProjectAuthorsChangeCbT) => void;
    onAdditionalContentChange?: (data: any) => void;
    onSetPresentationTag: (tag: any) => void;
    onSetRequiredTag: (tag: any) => void;
    onSetProjectTags: (tag: any) => void;
    onCopyLink?: (e: any) => void;
    onBilingualChange: () => void;
}


export const ProjectSettingsBlock: FC<IProjectSettingProps> = ({
    project,
    vTYPE,
    proTypeValue,
    proTypes,
    currentColor,
    requiredTags,
    isBilingual,
    moderationType,
    isTeamProject,
    isOuterCourse,
    onColorChange,
    toggleAgeRestricted,
    onSetPresentationTag,
    onSetRequiredTag,
    onBilingualChange,
    onCopyLink
}) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const isProHSE = AppTester.isProHSE();
    const isHseSpace = AppTester.isHseSpace();
    const isSpacePro = AppTester.isContext('s-pro');
    const isAgeytomesh = AppTester.isAgeytomesh();
    const isGeograffee = AppTester.isGeograffee();
    const modal = useModal();
    const toasts = useToasts();
    const is743 = useBreakpoint(LayoutConst.screen.s - 1);
    const isTeamEdit = useRouteMatch(LinkTo.teamEdit);
    const vtype = useSelector((state: any) => state.vt.vtype);
    const width = window.innerWidth;
    const { anchorLinks } = useSelector(HseContextSelector);
    const { field: { value: availability, onChange: onSetAvailability } } = useController({ name: 'availability' });
    const { field: { value: additionalContent, onChange: onSetAddContent } } = useController({ name: 'additionalContent' });
    const { setValue, watch } = useFormContext();
    const isPlaybook = project?.bookType === BookType.playbook;
    const isPro = AppTester.isProDeziiign() || isPlaybook;

    useEffect(() => {
        if (requiredTags && requiredTags.length > 0) {
            setValue('requiredTags', requiredTags, { shouldDirty: false });
        }
    }, []);

    const formRequiredTags = watch('requiredTags');
    useEffect(() => {
        if (formRequiredTags && JSON.stringify(formRequiredTags) !== JSON.stringify(requiredTags)) {
            onSetRequiredTag(formRequiredTags);
        }
    }, [formRequiredTags]);

    const isTransferContext = useMemo(() => vtype && vtype === 'free-type', [vtype])
    const shouldDisplayPresentationType = (isPro || isProHSE || isHseSpace) && !isTeamProject && !isPlaybook

    // Для лендинга курса настраивается страница, а не проект: другие подписи и без тегов
    const labels = useMemo(() => {
        const bilingualAction = isBilingual ? 'delete' : 'add';

        return isOuterCourse
            ? {
                settings: t('project_edit.settings_title'),
                structure: t('project_edit.page_structure'),
                bilingual: t(`bilingual_switcher.page_${bilingualAction}`),
            }
            : {
                settings: t('project_edit.project_title'),
                structure: t('project_edit.project_structure'),
                bilingual: t(`bilingual_switcher.presentation_${bilingualAction}`),
            };
    }, [isOuterCourse, isBilingual, t])

    const canEditAnnotation = useMemo(() => {
        return moderationType !== ModerationType.LMS && !!additionalContent?.annotation
    }, [moderationType])

    const onAnnotationVisibleChange = (flag: boolean) => {
        if (!canEditAnnotation) return;
        onSetAddContent && onSetAddContent(
            {
                ...(additionalContent ? additionalContent : {}),
                annotation: { ...additionalContent?.annotation, visible: flag }
            }
        )
    }

    const onAnchorSettingsChange = (data: any, field: string) => {
        //const isEnabling = field === 'anchorVisible' && !!data
        // if (isEnabling && !anchorLinks?.length) {
        //     toasts.error(t('project_edit.navigation.anchor_empty'))
        //     return;
        // }
        onSetAddContent && onSetAddContent(
            {
                ...(additionalContent ? additionalContent : {}),
                anchors: { ...additionalContent?.anchors, [field]: data, anchorType: AnchorType.popup }
            }
        )
    }



    return (
        <Right vtype={vTYPE} gap={isPlaybook ? 32 : 24}>
            <ProjectEditGrid>
            <SettingsLabel>{labels.settings}</SettingsLabel>
                {!isTransferContext && <ProjectStructureField label={labels.structure} />}
                {(isTeamProject || isPro || isProHSE) && !isPlaybook && (
                    <BilingWrapper direction="column">
                        <CommonCheckbox
                            label={t('project_edit.navigation.use_anchor_menu')}
                            checked={!!additionalContent?.anchors?.anchorVisible}
                            dark={!isGeograffee}
                            hintText={t("hints.anchor")}
                            onClick={() => onAnchorSettingsChange(!additionalContent?.anchors?.anchorVisible || false, 'anchorVisible')}
                        />
                    </BilingWrapper>
                )}
                <AnnotationField />
                <FlexBox>
                    <PickerWrapper>
                        <ColorPicker
                            mobileLeftOffset={width < 325 ? 115 : width > 363 ? 180 : 0}
                            value={currentColor}
                            onChange={onColorChange}
                            vTYPE={vTYPE}
                        />
                    </PickerWrapper>
                </FlexBox>
                {isPro && !isMobile && <PresentationAuthorsSelect />}
                {isPlaybook && (
                    <FlexBox direction={'column'} directionMobile='column' width={'100%'} widthMobile={'100%'}>
                        <BookChaptersField />
                    </FlexBox>
                )}
                {shouldDisplayPresentationType && (
                    <FlexBox id="presentation-structure-field">
                        <CommonInput
                            value={proTypeValue}
                            type={INPUT_TYPES.dropdown}
                            onChange={(option: any) => {
                                onSetPresentationTag(option.value);
                            }}
                            options={proTypes}
                            placeholder={t('teams.adminPage.fields.select_placeholder')}
                            label={t('project_edit.presentation_type')}
                            orientation="column"
                            required
                        />
                    </FlexBox>
                )}
            </ProjectEditGrid>
            {isPlaybook && <BookReproductionsField />}
            <ProjectEditGrid>
                {isPlaybook && (<SettingsLabel>{t('project_edit.additional_parameters')}</SettingsLabel>)}
                {isPlaybook && (<BookTagsField />)}
                {!isPlaybook && !isOuterCourse && !isTeamProject && (
                    <>
                        <ColumnLabelGroup>
                            <CommonLabelText>
                                {t('project_edit.required_tags')}
                                <LabelRedStar style={{ marginLeft: 4, marginTop: is743 ? 2 : 5, marginBottom: 0 }}>*</LabelRedStar>
                            </CommonLabelText>
                            <RequiredTagSelect noLabel />
                        </ColumnLabelGroup>
                        <ColumnLabelGroup>
                            <CommonLabelText>
                                {t('project_edit.tags')}
                            </CommonLabelText>
                            <TagSelect noLabel />
                        </ColumnLabelGroup>
                    </>
                )}
                {!!toggleAgeRestricted && (
                    <ProjectEditPageGridRowBase>
                        <CheckboxField
                            name="isAgeRestricted"
                            label={t('project_edit.age_restricted')}
                        />
                    </ProjectEditPageGridRowBase>
                )}
                {!isTeamEdit && (
                    <FlexBox direction={'column'} directionMobile='column' width={'100%'} widthMobile={'100%'}>
                        <Availability
                            availability={availability ?? ''}
                            onChange={(val) => onSetAvailability(val)}
                            direction="column"
                            //@ts-ignore
                            onCopy={onCopyLink}
                            hideLabel
                        />
                    </FlexBox>
                )}
                <ContentWrapper disableOffset={!isTeamEdit} offsetTop={isTeamEdit ? 24 : 0} disableRightOffset>
                    <BilingualSwitcher
                        isBilingual={isBilingual}
                        onClick={onBilingualChange}
                        text={labels.bilingual}
                    />
                </ContentWrapper>
            </ProjectEditGrid>
        </Right>
    )

}import { CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useIsMobile, useToasts, CommonLabelText } from "@brg/ui";
import { FC, useMemo, useEffect } from "react";
import { ContentWrapper, PickerWrapper, Right, SettingsLabel } from "../styles";
import { AppTester } from "supporting";
import { ProjectAuthorsChangeCbT } from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { useTranslation } from "react-i18next";
import { AddButton, ColumnLabelGroup, ProjectEditPageGridRowBase } from "shared/pages/projectEdit/styles";
import { ProjectEditPageGridLabel } from "shared/components/ProjectEditPageGrid";
import { ProjectEditPageGridInput } from "_deziiign/books/pages/BookEdit/styles";
import { Input } from "@brg/input";
import { LabelRedStar } from "shared/pages/designerEditView/styles";
import { bTag, LayoutConst, useModal } from "core";
import { AnchorType, IAddProjectCoverContent, Tag } from "@brg/api";
import { AGEYTOMESH_TAG_ID } from "shared/constants/misc";
import { ProjectAddButton } from "shared/pages/projectEdit/ProjectAddButton";
import { BilingWrapper, ContentAddContainer, ProjectEditGrid } from "shared/pages/projectEdit/styles";
import { MAX_PLACES_TAG_COUNT, MAX_TAG_COUNT } from "shared/pages/projectEdit/constants";
import { BTag } from "core/components";
import { isRequired } from "shared/modals/selectTagsModal/support/isRequired";
import { useRouteMatch } from "react-router-dom";

import { LinkTo } from "shared/constants/linkTo";
import { AuthorT } from "shared/pages/projectEdit/types";
import ColorPicker from "../../ColorPicker";
import ViewTypeController from "shared/components/ViewTypeController/ViewTypeController";
import { BilingualSwitcher } from "shared/components/BilingualSwitcher/BilingualSwitcher";
import { useSelector } from "react-redux";
import { BTagsGridNew } from "../../../../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import { HseContextSelector } from "shared/reducers/hseCurrentContext";
import { Availability } from "../../Availability/Availability";
import PresentationAuthorsSelect from "shared/pages/projectEdit/fields/Designers/PresentationAuthorsSelect";
import CheckboxField from "shared/pages/projectEdit/fields/CheckboxField";
import TagSelect from "shared/pages/projectEdit/fields/Tags/TagSelect";
import RequiredTagSelect from "shared/pages/projectEdit/fields/Tags/RequiredTagSelect";
import { useController, useFormContext } from "react-hook-form";
import { ModerationType } from "shared/pages/teams/const/ModerationTypes";
import AnnotationField from "shared/components/AnnotationField/AnnotationField";
import { BookType } from "_deziiign/books/constants";
import BookChaptersField from "shared/pages/projectEdit/fields/Book/BookChaptersField";
import BookReproductionsField from "shared/pages/projectEdit/fields/Book/BookReproductionsField";
import ProjectStructureField from "shared/pages/projectEdit/fields/ProjectStructureField";
import BookTagsField from "shared/pages/projectEdit/fields/Book/BookTagsField";
import { CommonCheckbox } from "shared/components/CommonCheckbox/CommonCheckbox";


interface IProjectSettingProps {
    project: any;
    vTYPE: string;
    additionalContent?: IAddProjectCoverContent;
    currentColor: string;
    proTypeValue: any;
    proTypes: any[];
    requiredTags: Tag[];
    projectTags: Tag[];
    tagEntity?: string[];
    coAuthors?: AuthorT[];
    contextRequiredTags?: string[];
    hasEducationTag?: boolean;
    tagEntityRequired?: string[];
    isAgeRestricted?: boolean;
    availability?: string;
    isBilingual: boolean;
    isTeamProject?: boolean;
    moderationType?: ModerationType;
    /** Лендинг курса образовательной программы — настраивается как страница, а не как проект */
    isOuterCourse?: boolean;
    toggleAgeRestricted?: (e: any) => void;
    onColorChange: (val: string) => void;
    onSetAvailability?: (availability: string) => void;
    onCoauthorChange?: (data: ProjectAuthorsChangeCbT) => void;
    onAdditionalContentChange?: (data: any) => void;
    onSetPresentationTag: (tag: any) => void;
    onSetRequiredTag: (tag: any) => void;
    onSetProjectTags: (tag: any) => void;
    onCopyLink?: (e: any) => void;
    onBilingualChange: () => void;
}


export const ProjectSettingsBlock: FC<IProjectSettingProps> = ({
    project,
    vTYPE,
    proTypeValue,
    proTypes,
    currentColor,
    requiredTags,
    isBilingual,
    moderationType,
    isTeamProject,
    isOuterCourse,
    onColorChange,
    toggleAgeRestricted,
    onSetPresentationTag,
    onSetRequiredTag,
    onBilingualChange,
    onCopyLink
}) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const isProHSE = AppTester.isProHSE();
    const isHseSpace = AppTester.isHseSpace();
    const isSpacePro = AppTester.isContext('s-pro');
    const isAgeytomesh = AppTester.isAgeytomesh();
    const isGeograffee = AppTester.isGeograffee();
    const modal = useModal();
    const toasts = useToasts();
    const is743 = useBreakpoint(LayoutConst.screen.s - 1);
    const isTeamEdit = useRouteMatch(LinkTo.teamEdit);
    const vtype = useSelector((state: any) => state.vt.vtype);
    const width = window.innerWidth;
    const { anchorLinks } = useSelector(HseContextSelector);
    const { field: { value: availability, onChange: onSetAvailability } } = useController({ name: 'availability' });
    const { field: { value: additionalContent, onChange: onSetAddContent } } = useController({ name: 'additionalContent' });
    const { setValue, watch } = useFormContext();
    const isPlaybook = project?.bookType === BookType.playbook;
    const isPro = AppTester.isProDeziiign() || isPlaybook;

    useEffect(() => {
        if (requiredTags && requiredTags.length > 0) {
            setValue('requiredTags', requiredTags, { shouldDirty: false });
        }
    }, []);

    const formRequiredTags = watch('requiredTags');
    useEffect(() => {
        if (formRequiredTags && JSON.stringify(formRequiredTags) !== JSON.stringify(requiredTags)) {
            onSetRequiredTag(formRequiredTags);
        }
    }, [formRequiredTags]);

    const isTransferContext = useMemo(() => vtype && vtype === 'free-type', [vtype])
    const shouldDisplayPresentationType = (isPro || isProHSE || isHseSpace) && !isTeamProject && !isPlaybook

    // Для лендинга курса настраивается страница, а не проект: другие подписи и без тегов
    const labels = useMemo(() => {
        const bilingualAction = isBilingual ? 'delete' : 'add';

        return isOuterCourse
            ? {
                settings: t('project_edit.settings_title'),
                structure: t('project_edit.page_structure'),
                bilingual: t(`bilingual_switcher.page_${bilingualAction}`),
            }
            : {
                settings: t('project_edit.project_title'),
                structure: t('project_edit.project_structure'),
                bilingual: t(`bilingual_switcher.presentation_${bilingualAction}`),
            };
    }, [isOuterCourse, isBilingual, t])

    const canEditAnnotation = useMemo(() => {
        return moderationType !== ModerationType.LMS && !!additionalContent?.annotation
    }, [moderationType])

    const onAnnotationVisibleChange = (flag: boolean) => {
        if (!canEditAnnotation) return;
        onSetAddContent && onSetAddContent(
            {
                ...(additionalContent ? additionalContent : {}),
                annotation: { ...additionalContent?.annotation, visible: flag }
            }
        )
    }

    const onAnchorSettingsChange = (data: any, field: string) => {
        //const isEnabling = field === 'anchorVisible' && !!data
        // if (isEnabling && !anchorLinks?.length) {
        //     toasts.error(t('project_edit.navigation.anchor_empty'))
        //     return;
        // }
        onSetAddContent && onSetAddContent(
            {
                ...(additionalContent ? additionalContent : {}),
                anchors: { ...additionalContent?.anchors, [field]: data, anchorType: AnchorType.popup }
            }
        )
    }



    return (
        <Right vtype={vTYPE} gap={isPlaybook ? 32 : 24}>
            <ProjectEditGrid>
            <SettingsLabel>{labels.settings}</SettingsLabel>
                {!isTransferContext && <ProjectStructureField label={labels.structure} />}
                {(isTeamProject || isPro || isProHSE) && !isPlaybook && (
                    <BilingWrapper direction="column">
                        <CommonCheckbox
                            label={t('project_edit.navigation.use_anchor_menu')}
                            checked={!!additionalContent?.anchors?.anchorVisible}
                            dark={!isGeograffee}
                            hintText={t("hints.anchor")}
                            onClick={() => onAnchorSettingsChange(!additionalContent?.anchors?.anchorVisible || false, 'anchorVisible')}
                        />
                    </BilingWrapper>
                )}
                <AnnotationField />
                <FlexBox>
                    <PickerWrapper>
                        <ColorPicker
                            mobileLeftOffset={width < 325 ? 115 : width > 363 ? 180 : 0}
                            value={currentColor}
                            onChange={onColorChange}
                            vTYPE={vTYPE}
                        />
                    </PickerWrapper>
                </FlexBox>
                {isPro && !isMobile && <PresentationAuthorsSelect />}
                {isPlaybook && (
                    <FlexBox direction={'column'} directionMobile='column' width={'100%'} widthMobile={'100%'}>
                        <BookChaptersField />
                    </FlexBox>
                )}
                {shouldDisplayPresentationType && (
                    <FlexBox id="presentation-structure-field">
                        <CommonInput
                            value={proTypeValue}
                            type={INPUT_TYPES.dropdown}
                            onChange={(option: any) => {
                                onSetPresentationTag(option.value);
                            }}
                            options={proTypes}
                            placeholder={t('teams.adminPage.fields.select_placeholder')}
                            label={t('project_edit.presentation_type')}
                            orientation="column"
                            required
                        />
                    </FlexBox>
                )}
            </ProjectEditGrid>
            {isPlaybook && <BookReproductionsField />}
            <ProjectEditGrid>
                {isPlaybook && (<SettingsLabel>{t('project_edit.additional_parameters')}</SettingsLabel>)}
                {isPlaybook && (<BookTagsField />)}
                {!isPlaybook && !isOuterCourse && !isTeamProject && (
                    <>
                        <ColumnLabelGroup>
                            <CommonLabelText>
                                {t('project_edit.required_tags')}
                                <LabelRedStar style={{ marginLeft: 4, marginTop: is743 ? 2 : 5, marginBottom: 0 }}>*</LabelRedStar>
                            </CommonLabelText>
                            <RequiredTagSelect noLabel />
                        </ColumnLabelGroup>
                        <ColumnLabelGroup>
                            <CommonLabelText>
                                {t('project_edit.tags')}
                            </CommonLabelText>
                            <TagSelect noLabel />
                        </ColumnLabelGroup>
                    </>
                )}
                {!!toggleAgeRestricted && (
                    <ProjectEditPageGridRowBase>
                        <CheckboxField
                            name="isAgeRestricted"
                            label={t('project_edit.age_restricted')}
                        />
                    </ProjectEditPageGridRowBase>
                )}
                {!isTeamEdit && (
                    <FlexBox direction={'column'} directionMobile='column' width={'100%'} widthMobile={'100%'}>
                        <Availability
                            availability={availability ?? ''}
                            onChange={(val) => onSetAvailability(val)}
                            direction="column"
                            //@ts-ignore
                            onCopy={onCopyLink}
                            hideLabel
                        />
                    </FlexBox>
                )}
                <ContentWrapper disableOffset={!isTeamEdit} offsetTop={isTeamEdit ? 24 : 0} disableRightOffset>
                    <BilingualSwitcher
                        isBilingual={isBilingual}
                        onClick={onBilingualChange}
                        text={labels.bilingual}
                    />
                </ContentWrapper>
            </ProjectEditGrid>
        </Right>
    )

}